import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, Clock } from 'lucide-react';

interface DateTimePickerProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}

const monthFormatter = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });
const inputFormatter = new Intl.DateTimeFormat(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const weekdayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const hours = Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, '0'));
const minutes = Array.from({ length: 12 }, (_, minute) => String(minute * 5).padStart(2, '0'));

const pad = (value: number) => String(value).padStart(2, '0');

const parseDateTime = (value: string): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toDateTimeValue = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;

export const DateTimePicker: React.FC<DateTimePickerProps> = ({ id, label, value, onChange }) => {
  const selectedDate = useMemo(() => parseDateTime(value), [value]);
  const [isOpen, setIsOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const date = selectedDate ?? new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });

  useEffect(() => {
    if (selectedDate) {
      setVisibleMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
    }
  }, [selectedDate]);

  const calendarDays = useMemo(() => {
    const firstWeekday = visibleMonth.getDay();
    const daysInMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0).getDate();
    const cells = Array.from({ length: firstWeekday + daysInMonth }, (_, index) => {
      const day = index - firstWeekday + 1;
      return day > 0 ? new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), day) : null;
    });

    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [visibleMonth]);

  const selectDay = (date: Date) => {
    const next = selectedDate ? new Date(selectedDate) : new Date();
    next.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    onChange(toDateTimeValue(next));
  };

  const updateTime = (part: 'hours' | 'minutes', nextValue: string) => {
    const next = selectedDate ? new Date(selectedDate) : new Date();
    if (part === 'hours') next.setHours(Number(nextValue));
    else next.setMinutes(Number(nextValue));
    next.setSeconds(0, 0);
    onChange(toDateTimeValue(next));
  };

  const isSameDay = (date: Date, compare: Date | null) => Boolean(compare
    && date.getFullYear() === compare.getFullYear()
    && date.getMonth() === compare.getMonth()
    && date.getDate() === compare.getDate());

  return (
    <div className="relative">
      <label htmlFor={id} className="mb-1 block text-xs font-bold uppercase tracking-wide text-zinc-600">{label}</label>
      <button
        id={id}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className={`flex min-h-11 w-full items-center justify-between rounded-xl border bg-white px-3 py-2.5 text-left text-sm transition-colors ${isOpen ? 'border-amber-500 ring-2 ring-amber-100' : 'border-zinc-300 hover:border-zinc-400'}`}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <span className={selectedDate ? 'text-zinc-900' : 'text-zinc-400'}>{selectedDate ? inputFormatter.format(selectedDate) : 'Select date and time'}</span>
        <CalendarDays className="h-4 w-4 shrink-0 text-zinc-600" />
      </button>

      {isOpen && (
        <div className="absolute z-30 mt-2 w-[320px] max-w-[calc(100vw-2rem)] rounded-xl border border-zinc-200 bg-white p-3 shadow-xl shadow-zinc-900/15" role="dialog" aria-label={`${label} picker`}>
          <div className="flex items-center justify-between">
            <button type="button" onClick={() => setVisibleMonth((month) => new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="rounded-lg p-2 text-zinc-600 hover:bg-zinc-100" aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></button>
            <p className="text-sm font-black text-zinc-900">{monthFormatter.format(visibleMonth)}</p>
            <button type="button" onClick={() => setVisibleMonth((month) => new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="rounded-lg p-2 text-zinc-600 hover:bg-zinc-100" aria-label="Next month"><ChevronRight className="h-4 w-4" /></button>
          </div>

          <div className="mt-2 grid grid-cols-7 gap-0.5 text-center">
            {weekdayLabels.map((weekday) => <span key={weekday} className="py-1 text-[9px] font-black uppercase text-zinc-400">{weekday}</span>)}
            {calendarDays.map((date, index) => {
              const isToday = isSameDay(date ?? new Date(0), new Date());
              const isSelected = date ? isSameDay(date, selectedDate) : false;
              return date ? (
                <button
                  key={date.toISOString()}
                  type="button"
                  onClick={() => selectDay(date)}
                  className={`mx-auto flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold transition-colors ${isSelected ? 'bg-amber-500 text-zinc-950' : isToday ? 'bg-amber-50 text-amber-800' : 'text-zinc-700 hover:bg-zinc-100'}`}
                >
                  {date.getDate()}
                </button>
              ) : <span key={`blank-${index}`} />;
            })}
          </div>

          <div className="mt-3 flex items-center gap-2 border-t border-zinc-100 pt-3">
            <Clock className="h-4 w-4 text-zinc-500" />
            <select value={selectedDate ? pad(selectedDate.getHours()) : ''} onChange={(event) => updateTime('hours', event.target.value)} className="rounded-lg border border-zinc-300 bg-white px-2 py-2 text-sm text-zinc-800">
              <option value="" disabled>Hour</option>
              {hours.map((hour) => <option key={hour} value={hour}>{hour}</option>)}
            </select>
            <span className="font-bold text-zinc-500">:</span>
            <select value={selectedDate ? pad(selectedDate.getMinutes() - (selectedDate.getMinutes() % 5)) : ''} onChange={(event) => updateTime('minutes', event.target.value)} className="rounded-lg border border-zinc-300 bg-white px-2 py-2 text-sm text-zinc-800">
              <option value="" disabled>Minute</option>
              {minutes.map((minute) => <option key={minute} value={minute}>{minute}</option>)}
            </select>
            <button type="button" onClick={() => setIsOpen(false)} className="ml-auto rounded-lg bg-zinc-950 px-3 py-2 text-xs font-black uppercase tracking-wide text-white hover:bg-zinc-800">Done</button>
          </div>
        </div>
      )}
    </div>
  );
};
